import mmLogoWht from '@/assets/logos/mm-logo_wht.svg';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { UserRole } from '@/types/roles';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Sidebar as PrimeSidebar } from 'primereact/sidebar';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Flatten roles across every JWT org membership, not just the first one —
  // SuperAdmin (or any other role) can live on an org other than the home organization and still apply.
  const userRoles: string[] = useMemo(() => {
    return user?.organizations?.flatMap(org => org.roles) ?? [];
  }, [user]);

  const hasSuperAdminRole = useMemo(() => {
    return userRoles.includes(UserRole.SUPER_ADMIN);
  }, [userRoles]);

  const hasOrganizationAdminAccess = useMemo(() => {
    return (
      userRoles.includes(UserRole.SUPER_ADMIN) ||
      userRoles.includes(UserRole.ORGANIZATION_ADMIN)
    );
  }, [userRoles]);

  // Build menu items
  const menuItems: MenuItem[] = useMemo(() => {
    const items = [
      {
        label: t('layout.sidebar.projects'),
        icon: 'pi pi-folder',
        command: () => {
          navigate('/projects');
          if (isMobile) onClose();
        },
        className: location.pathname.startsWith('/projects')
          ? 'active-menu-item'
          : '',
      },
      {
        label: t('layout.sidebar.surveys'),
        icon: 'pi pi-list',
        command: () => {
          navigate('/surveys');
          if (isMobile) onClose();
        },
        className: location.pathname.startsWith('/surveys')
          ? 'active-menu-item'
          : '',
      },
    ];

    // Add Organizations menu item for admins of any org
    if (hasOrganizationAdminAccess) {
      items.push({
        label: t('layout.sidebar.organizations'),
        icon: 'pi pi-sitemap',
        command: () => {
          navigate('/admin/organizations');
          if (isMobile) onClose();
        },
        className: location.pathname.startsWith('/admin/organizations')
          ? 'active-menu-item'
          : '',
      });
    }

    // Add Prompts menu item if user has SuperAdmin role
    if (hasSuperAdminRole) {
      items.push({
        label: t('layout.sidebar.prompts'),
        icon: 'pi pi-file-edit',
        command: () => {
          navigate('/prompts');
          if (isMobile) onClose();
        },
        className: location.pathname.startsWith('/prompts')
          ? 'active-menu-item'
          : '',
      });
    }

    // Adds Style Guide menu item if user has SuperAdmin role
    if (hasSuperAdminRole) {
      items.push({
        label: t('layout.sidebar.styleGuide'),
        icon: 'pi pi-book',
        command: () => {
          navigate('/style-guide');
          if (isMobile) onClose();
        },
        className: location.pathname.startsWith('/style-guide')
          ? 'active-menu-item'
          : '',
      });
    }

    return items;
  }, [
    navigate,
    isMobile,
    onClose,
    location.pathname,
    t,
    hasSuperAdminRole,
    hasOrganizationAdminAccess,
  ]);

  const sidebarHeader = (
    <div className='flex items-center justify-center p-4 border-b border-white/10'>
      <img
        src={mmLogoWht}
        alt={t('layout.appName')}
        className='w-full max-w-[200px]'
      />
    </div>
  );

  // Desktop: render persistent sidebar
  if (!isMobile) {
    return (
      <nav
        className='sidebar flex flex-col w-60 h-screen bg-stone-800 border-r border-surface'
        aria-label={t('layout.sidebar.navigationAriaLabel')}
      >
        {sidebarHeader}
        <div className='sidebar-nav'>
          <Menu model={menuItems} className='sidebar-menu' />
        </div>
      </nav>
    );
  }

  // Mobile: render overlay sidebar
  return (
    <PrimeSidebar
      visible={isOpen}
      onHide={onClose}
      position='left'
      header={sidebarHeader}
      closeOnEscape
      className='sidebar !bg-stone-800 !w-60'
      ariaCloseLabel={t('layout.sidebar.closeAriaLabel')}
    >
      <nav
        className='sidebar-nav'
        aria-label={t('layout.sidebar.navigationAriaLabel')}
      >
        <Menu model={menuItems} className='sidebar-menu' />
      </nav>
    </PrimeSidebar>
  );
};
